import { z } from "zod";

export const PRODUCTS = ["browser-guard", "plc", "glv", "precheck"] as const;
export const Product = z.enum(PRODUCTS).meta({ id: "Product" });
export type Product = z.infer<typeof Product>;

export const PURPOSES = ["login", "deposit", "bet", "withdrawal", "other"] as const;
export const Purpose = z.enum(PURPOSES).meta({ id: "Purpose" });
export type Purpose = z.infer<typeof Purpose>;

/** `review` is reserved for future case management and is never emitted (clients treat unknown as deny). */
export const Decision = z.enum(["allow", "deny"]).meta({ id: "Decision" });
export type Decision = z.infer<typeof Decision>;

export const CheckResult = z.enum(["pass", "fail", "not_evaluated"]).meta({ id: "CheckResult" });
export type CheckResult = z.infer<typeof CheckResult>;

export const CheckSeverity = z.enum(["blocking", "advisory"]).meta({ id: "CheckSeverity" });
export type CheckSeverity = z.infer<typeof CheckSeverity>;

export const CHECK_IDS = [
  "integrity",
  "jurisdiction",
  "player_block",
  "device_block",
  "automation",
  "travel_speed",
] as const;
export const CheckId = z.enum(CHECK_IDS).meta({ id: "CheckId" });
export type CheckId = z.infer<typeof CheckId>;

/**
 * The operator environment an installation serves (ADR 0011): `staging` or `production`, one
 * installation per environment. `sandbox` was renamed `staging` and is no longer a value.
 */
export const OPERATOR_ENVIRONMENT_VALUES = ["staging", "production"] as const;
export const OperatorEnvironment = z
  .enum(OPERATOR_ENVIRONMENT_VALUES)
  .meta({ id: "OperatorEnvironment" });
export type OperatorEnvironment = z.infer<typeof OperatorEnvironment>;

/**
 * Environment marker of a `client_id` (`oa_stg_…` staging, `oa_prd_…` production). A marker of the
 * other installation's environment is refused with `401 environment_mismatch`; any other prefix
 * (the legacy `oa_sbx_` included) is an unknown credential (`401 invalid_client`).
 */
export const CLIENT_ID_PREFIXES = {
  staging: "oa_stg_",
  production: "oa_prd_",
} as const satisfies Record<OperatorEnvironment, string>;
export type ClientIdPrefix = (typeof CLIENT_ID_PREFIXES)[OperatorEnvironment];

/** Operator-defined, stable, pseudonymous references. PII detection lives in the domain value object. */
export const REF_PATTERN = /^[A-Za-z0-9._:-]{1,128}$/;
export const PlayerRef = z.string().regex(REF_PATTERN).meta({ id: "PlayerRef" });
export const PlayerSessionRef = z.string().regex(REF_PATTERN).meta({ id: "PlayerSessionRef" });

export const Uuid = z.uuid();
export const IsoDateTime = z.iso.datetime({ offset: true });
